import React from 'react';

/**
 * RouteErrorBoundary
 *
 * Contains a render error to the page that threw it.  Without a boundary React
 * unmounts the whole tree on an uncaught render error, which left users on a
 * completely blank screen (no sidebar, no navigation) when a single page
 * crashed.  The navigation shell stays usable, the error is still logged, and
 * the boundary resets itself when the route location changes (`resetKey`) so
 * navigating away and back retries the page.
 */
class RouteErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { error: null };
		this.handleRetry = this.handleRetry.bind(this);
	}

	static getDerivedStateFromError(error) {
		return { error };
	}

	componentDidUpdate(prevProps) {
		const { resetKey } = this.props;
		const { error } = this.state;
		if (error && prevProps.resetKey !== resetKey) {
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({ error: null });
		}
	}

	componentDidCatch(error, info) {
		// eslint-disable-next-line no-console
		console.error('[RouteErrorBoundary] page render failed:', error, info && info.componentStack);
	}

	handleRetry() {
		this.setState({ error: null });
	}

	render() {
		const { error } = this.state;
		const { children } = this.props;
		if (!error) { return children; }
		return (
			<div className="cg-route-error" role="alert" style={{ padding: 'var(--tk-spacing-6, 32px)' }}>
				<h3>Something went wrong loading this page.</h3>
				<p>The rest of the application is still available. You can try again or reload the page.</p>
				<button type="button" onClick={this.handleRetry}>Try again</button>
				{' '}
				<button type="button" onClick={() => window.location.reload()}>Reload page</button>
			</div>
		);
	}
}

export default RouteErrorBoundary;
